import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet } from 'react-router';
import type { AdminUser } from '@repo/shared-types';
import { Button } from '@repo/ui/src/components/ui/button';
import { adminFetchApi, type ApiError } from '../lib/api';
import { useAdminAuthStore } from '../stores/adminAuthStore';

export function AdminAuthGuard() {
  const accessToken = useAdminAuthStore((s) => s.accessToken);
  const isLoading = useAdminAuthStore((s) => s.isLoading);
  const hydrateFromStorage = useAdminAuthStore((s) => s.hydrateFromStorage);
  const clearAuth = useAdminAuthStore((s) => s.clearAuth);

  // Hydrate auth from localStorage on mount.
  useEffect(() => {
    hydrateFromStorage();
  }, [hydrateFromStorage]);

  // Validate the session once against /admin/auth/me.
  const meQuery = useQuery<AdminUser, ApiError>({
    queryKey: ['admin', 'auth', 'me'],
    queryFn: () => adminFetchApi<AdminUser>('/admin/auth/me'),
    enabled: !!accessToken,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Only a genuine auth failure (401) should log the admin out. A raw network
  // failure (API down) throws a plain Error with no `status` — treating that
  // the same as a 401 would force-logout an admin the moment the API they're
  // trying to investigate becomes unreachable.
  const isAuthError = meQuery.error?.status === 401;

  useEffect(() => {
    if (isAuthError) {
      clearAuth();
    }
  }, [isAuthError, clearAuth]);

  if (isLoading || (!!accessToken && meQuery.isLoading)) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  if (!accessToken || isAuthError) {
    return <Navigate to="/login" replace />;
  }

  if (meQuery.isError) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-surface-50 px-4 text-center">
        <div className="max-w-sm rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">
            Không thể kết nối đến máy chủ. Vui lòng kiểm tra kết nối mạng và thử lại.
          </p>
        </div>
        <Button type="button" onClick={() => meQuery.refetch()}>
          Thử lại
        </Button>
      </div>
    );
  }

  return <Outlet />;
}
